import { useMemo } from 'react';
import {
    ELASTIC_BUTTON,
    ELASTIC_BUTTON_LINGUAL,
    buttonTrianglePath,
    elasticButtonStyle,
    placementColor
} from './model';
import type { ElasticPlacement } from './model';
import {
    bandPathD,
    orderBadgePositions,
    placementArrowHeads,
    placementPoints
} from './bandGeometry';
import { lingualToothSet } from './persistence';
import {
    ODONTOGRAM_VIEWBOX,
    toothButtonAnchor,
    toothGlyph,
    toothLabel,
    toothLabelPos,
    toothLayout,
    toothOffset
} from './toothGeometry';

interface ElasticsChartProps {
    placements: ElasticPlacement[];
    /** Teeth tapped so far for the in-progress band (empty when not drawing). */
    selected: number[];
    drawing: boolean;
    readOnly: boolean;
    onToothClick: (fdi: number) => void;
    onRemoveBand: (id: string) => void;
    /** Toggle a placed button between buccal and lingual (click on the button). */
    onButtonClick?: (fdi: number) => void;
    /** Bare SVG with thicker strokes and larger labels, legible when capped near 520px wide. */
    compact?: boolean;
}

const TEETH = toothLayout();
/** Neutral color for the in-progress (not-yet-closed) band preview. */
const DRAW_COLOR = '#111827';

// SVG units: the viewBox is 1221 wide, so a compact chart renders at roughly 0.43x.
const CHART_SIZES = {
    regular: {
        label: 16,
        tooth: 2.6,
        band: 3.6,
        badgeR: 12.5,
        badgeFont: 15,
        buttonScale: 1
    },
    compact: {
        label: 25,
        tooth: 3.2,
        band: 5,
        badgeR: 19,
        badgeFont: 22,
        buttonScale: 1.4
    }
} as const;

// The shared arrowhead geometry stays as the PDF draws it; an outline enlarges it on screen.
const COMPACT_ARROW_OUTLINE = 6;

// Pure presentation of the odontogram SVG: state and mutation live in the parent Odontogram,
// this only derives geometry and reports clicks.
export function ElasticsChart({
    placements,
    selected,
    drawing,
    readOnly,
    onToothClick,
    onRemoveBand,
    onButtonClick,
    compact = false
}: ElasticsChartProps) {
    const size = CHART_SIZES[compact ? 'compact' : 'regular'];
    const buttonsInteractive = !readOnly && !drawing && !!onButtonClick;
    const bands = useMemo(() => {
        const badges = orderBadgePositions(placements);
        return placements.map((placement, index) => {
            const points = placementPoints(placement);
            const color = placementColor(placement);
            return {
                placement,
                points,
                d: bandPathD(points),
                color,
                order: index + 1,
                arrows: placementArrowHeads(placement),
                centroid: badges[index]
            };
        });
    }, [placements]);

    // A button is drawn once on each tooth that a committed elastic lands on,
    // near the tooth's cervical (top/bottom) end.
    const buttonAnchors = useMemo(() => {
        const lingual = lingualToothSet(placements);
        const seen = new Set<number>();
        const anchors: Array<{ fdi: number; x: number; y: number; lingual: boolean }> = [];
        for (const placement of placements) {
            for (const fdi of placement.teeth) {
                if (seen.has(fdi)) continue;
                seen.add(fdi);
                anchors.push({ fdi, ...toothButtonAnchor(fdi), lingual: lingual.has(fdi) });
            }
        }
        return anchors;
    }, [placements]);

    // Live preview of the band being drawn - an open polyline through the tapped
    // teeth's buttons, with a ring on the first (the close target).
    const draftPoints = useMemo(
        () => selected.map((fdi) => ({ fdi, ...toothButtonAnchor(fdi) })),
        [selected]
    );
    const draftPathD = useMemo(() => {
        if (draftPoints.length < 2) return '';
        return (
            `M ${draftPoints[0].x} ${draftPoints[0].y} ` +
            draftPoints
                .slice(1)
                .map((p) => `L ${p.x} ${p.y}`)
                .join(' ')
        );
    }, [draftPoints]);

    const chart = (
        <svg
            viewBox={`${ODONTOGRAM_VIEWBOX.minX} ${ODONTOGRAM_VIEWBOX.minY} ${ODONTOGRAM_VIEWBOX.width} ${ODONTOGRAM_VIEWBOX.height}`}
            className="block h-auto w-full select-none"
            role="img"
            aria-label="Elastics odontogram"
        >
            {/* Teeth */}
            <g>
                {TEETH.map((tooth) => {
                    const isSel = selected.includes(tooth.fdi);
                    const glyph = toothGlyph(tooth.fdi);
                    const label = toothLabelPos(tooth.fdi);
                    const { dx, dy } = toothOffset(tooth.fdi);
                    return (
                        <g
                            key={tooth.fdi}
                            onClick={() => onToothClick(tooth.fdi)}
                            style={{ cursor: readOnly || !drawing ? 'default' : 'pointer' }}
                        >
                            <path
                                d={glyph.outline}
                                transform={`translate(${dx} ${dy})`}
                                fill={isSel ? '#1f2937' : '#ffffff'}
                                stroke={isSel ? '#111827' : '#8b96a3'}
                                strokeWidth={isSel ? 3.6 : size.tooth}
                                strokeLinejoin="round"
                            />
                            <text
                                x={label.x}
                                y={label.y}
                                textAnchor="middle"
                                dominantBaseline="central"
                                fontSize={size.label}
                                fontWeight={isSel ? 700 : 500}
                                fill={isSel ? '#111827' : '#94a0ad'}
                                style={{ pointerEvents: 'none' }}
                            >
                                {toothLabel(tooth.fdi)}
                            </text>
                        </g>
                    );
                })}
            </g>

            {/* Committed bands + order number over the teeth. Right-click to delete. */}
            <g>
                {bands.map(({ placement, d, color, order, arrows, centroid }) => (
                    <g
                        key={placement.id}
                        onContextMenu={
                            readOnly
                                ? undefined
                                : (e) => {
                                      e.preventDefault();
                                      onRemoveBand(placement.id);
                                  }
                        }
                        style={{ cursor: readOnly ? 'default' : 'context-menu' }}
                    >
                        {/* Invisible fat hit area so the thin band is easy to right-click. */}
                        {d && <path d={d} fill="none" stroke="transparent" strokeWidth={20} />}
                        {d && (
                            <path
                                d={d}
                                fill="none"
                                stroke={color}
                                strokeWidth={size.band}
                                strokeLinecap="round"
                                opacity={0.9}
                                style={{ pointerEvents: 'none' }}
                            />
                        )}
                        {arrows.map((head, i) => (
                            <path
                                key={`${placement.id}-arrow-${i}`}
                                d={head}
                                fill={color}
                                {...(compact && {
                                    stroke: color,
                                    strokeWidth: COMPACT_ARROW_OUTLINE,
                                    strokeLinejoin: 'round'
                                })}
                                style={{ pointerEvents: 'none' }}
                            />
                        ))}
                        {/* Install-order number, riding the band line (centroid) */}
                        <circle
                            cx={centroid.x}
                            cy={centroid.y}
                            r={size.badgeR}
                            fill={color}
                            stroke="#ffffff"
                            strokeWidth={2}
                        />
                        <text
                            x={centroid.x}
                            y={centroid.y}
                            textAnchor="middle"
                            dominantBaseline="central"
                            fontSize={size.badgeFont}
                            fontWeight={700}
                            fill="#ffffff"
                            style={{ pointerEvents: 'none' }}
                        >
                            {order}
                        </text>
                    </g>
                ))}
            </g>

            {/* Red circle = buccal, blue triangle = lingual: the shape tells them
                        apart on a black-and-white printout. */}
            <g style={{ pointerEvents: buttonsInteractive ? 'auto' : 'none' }}>
                {buttonAnchors.map((pt) => {
                    const style = elasticButtonStyle(pt.lingual ? 'lingual' : 'buccal');
                    const radius = style.radius * size.buttonScale;
                    const onClick = buttonsInteractive
                        ? (e: React.MouseEvent) => {
                              e.stopPropagation();
                              onButtonClick?.(pt.fdi);
                          }
                        : undefined;
                    const cursor = buttonsInteractive ? 'pointer' : 'default';
                    const title = pt.lingual
                        ? 'Lingual — click for buccal'
                        : 'Buccal — click for lingual';
                    if (style.shape === 'triangle') {
                        return (
                            <path
                                key={pt.fdi}
                                d={buttonTrianglePath(pt.x, pt.y, radius)}
                                fill={style.fill}
                                stroke={style.stroke}
                                strokeWidth={style.strokeWidth}
                                strokeLinejoin="round"
                                onClick={onClick}
                                style={{ cursor }}
                            >
                                {buttonsInteractive && <title>{title}</title>}
                            </path>
                        );
                    }
                    return (
                        <circle
                            key={pt.fdi}
                            cx={pt.x}
                            cy={pt.y}
                            r={radius}
                            fill={style.fill}
                            stroke={style.stroke}
                            strokeWidth={style.strokeWidth}
                            onClick={onClick}
                            style={{ cursor }}
                        >
                            {buttonsInteractive && <title>{title}</title>}
                        </circle>
                    );
                })}
            </g>

            {/* In-progress band preview (only while drawing) */}
            {drawing && draftPoints.length > 0 && (
                <g style={{ pointerEvents: 'none' }}>
                    {draftPathD && (
                        <path
                            d={draftPathD}
                            fill="none"
                            stroke={DRAW_COLOR}
                            strokeWidth={2.2}
                            strokeLinecap="round"
                            strokeDasharray="5 4"
                            opacity={0.8}
                        />
                    )}
                    {draftPoints.map((pt) => (
                        <circle
                            key={pt.fdi}
                            cx={pt.x}
                            cy={pt.y}
                            r={ELASTIC_BUTTON.radius}
                            fill={ELASTIC_BUTTON.fill}
                            stroke={ELASTIC_BUTTON.stroke}
                            strokeWidth={ELASTIC_BUTTON.strokeWidth}
                        />
                    ))}
                    {/* Ring on the first tooth = the close target. */}
                    {draftPoints.length >= 2 && (
                        <circle
                            cx={draftPoints[0].x}
                            cy={draftPoints[0].y}
                            r={ELASTIC_BUTTON.radius + 4}
                            fill="none"
                            stroke={DRAW_COLOR}
                            strokeWidth={1.6}
                            strokeDasharray="3 3"
                        />
                    )}
                </g>
            )}
        </svg>
    );
    if (compact) return chart;

    return (
        <div className="flex w-full flex-col gap-2">
            <div
                className={`relative w-full rounded-xl border border-gray-200 bg-white px-3 ${readOnly ? 'py-9' : 'py-3'}`}
            >
                <span className="absolute left-4 top-3 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    Upper arch
                </span>
                <span className="absolute bottom-3 left-4 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    Lower arch
                </span>
                {chart}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-[10px] text-gray-500">
                <span>Button, hook, cut-outs on:</span>
                <ButtonSideSwatches />
            </div>
        </div>
    );
}

// The button shape → side key; the shape (not only the color) survives a black-and-white printout.
export function ButtonSideSwatches() {
    return (
        <>
            <span className="flex items-center gap-1.5">
                <span
                    className="inline-block h-3 w-3 flex-shrink-0 rounded-full"
                    style={{
                        backgroundColor: ELASTIC_BUTTON.fill,
                        border: `1.5px solid ${ELASTIC_BUTTON.stroke}`
                    }}
                />
                <span className="font-semibold text-gray-600">Buccal</span>
            </span>
            <span className="flex items-center gap-1.5">
                <svg viewBox="0 0 12 12" className="h-3 w-3 flex-shrink-0" aria-hidden="true">
                    <path
                        d="M6 1 L11 10.5 L1 10.5 Z"
                        fill={ELASTIC_BUTTON_LINGUAL.fill}
                        stroke={ELASTIC_BUTTON_LINGUAL.stroke}
                        strokeWidth={1.5}
                        strokeLinejoin="round"
                    />
                </svg>
                <span className="font-semibold text-gray-600">Lingual</span>
            </span>
        </>
    );
}
